// Telling a tap apart from a drag that selected some text. Lifted as is from
// the Andon app's `lib/selection.ts`.
//
// WHY THIS IS NEEDED AT ALL
//
// An andon card is one big target: the whole thing opens the alert, which is
// right — a gloved thumb should not have to find a link. Once the text inside
// it is selectable (see `.selectable` in theme/tokens.css) those two wants
// collide, because selecting a pod number ends in a mouseup over the card, and
// a mouseup over a click target is a click. Without this, dragging across an
// AN number would open the alert and throw the selection away, so the feature
// would look broken in exactly the moment somebody used it.
//
// WHY NOT A DRAG-DISTANCE HEURISTIC
//
// The other way to do this is to record where the pointer went down and ignore
// the click if it moved more than a few pixels. It sounds simpler and it is
// worse: it fires on a scroll fling that happens to end on a card, it needs a
// threshold nobody can pick correctly for both a mouse and a gloved thumb, and
// it is wrong about the case that actually matters — a double-click, which
// selects a whole word while moving the pointer zero pixels.
//
// Asking the browser what is selected has none of those problems, because it is
// the actual question.

/**
 * Is there a live text selection inside this element?
 *
 * SCOPED TO THE ELEMENT, deliberately. A bare "is anything selected" would
 * mean that text highlighted somewhere else on the screen — in the thread, in
 * another card — silently stopped this card from opening, which is a far more
 * confusing bug than the one it fixes.
 *
 * Whitespace-only selections do not count: a stray drag that catches the gap
 * between two lines selects a newline, and refusing to open on that is
 * indistinguishable from the tap not working.
 */
export function selectingWithin(el: Node | null | undefined): boolean {
  if (!el || typeof window === 'undefined') return false;
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return false;
  if (selection.toString().trim().length === 0) return false;
  // `containsNode` with `partialContainment` — a selection that starts inside
  // the card and runs off the end of it is still this card's selection, and is
  // the ordinary result of dragging past the last line.
  return selection.containsNode(el, true);
}
